"use client";

import { useReportWebVitals } from "next/web-vitals";
import { analytics } from "@/lib/analytics/core";

/** 1-in-N session sampling. Web vitals fire ~5 times per page load (one per
 *  metric), which floods GA4 with low-signal hits. We pick once per session
 *  and either report everything or nothing, so a sampled session still
 *  produces a coherent metric set for analysis. */
const SAMPLE_RATE = 0.1;
const SESSION_SAMPLE_KEY = "bp-wv-sampled";

/** Skip FCP/TTFB — FCP closely tracks LCP and TTFB belongs in server logs.
 *  This drops volume by ~40% before sampling is even applied. */
const REPORTED_METRICS = new Set(["LCP", "CLS", "INP"]);

const sentIds = new Set<string>();

function getRating(name: string, value: number): string {
  switch (name) {
    case "CLS":
      return value <= 0.1 ? "good" : value <= 0.25 ? "needs-improvement" : "poor";
    case "FID":
    case "INP":
      return value <= 200 ? "good" : value <= 500 ? "needs-improvement" : "poor";
    case "LCP":
      return value <= 2500 ? "good" : value <= 4000 ? "needs-improvement" : "poor";
    case "FCP":
      return value <= 1800 ? "good" : value <= 3000 ? "needs-improvement" : "poor";
    case "TTFB":
      return value <= 800 ? "good" : value <= 1800 ? "needs-improvement" : "poor";
    default:
      return "unknown";
  }
}

function isSampledSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const cached = sessionStorage.getItem(SESSION_SAMPLE_KEY);
    if (cached !== null) return cached === "1";
    const sampled = Math.random() < SAMPLE_RATE;
    sessionStorage.setItem(SESSION_SAMPLE_KEY, sampled ? "1" : "0");
    return sampled;
  } catch {
    return Math.random() < SAMPLE_RATE;
  }
}

export function WebVitals() {
  useReportWebVitals((metric) => {
    if (!REPORTED_METRICS.has(metric.name)) return;
    if (sentIds.has(metric.id)) return;
    if (!isSampledSession()) return;
    sentIds.add(metric.id);

    analytics.track("web_vitals", {
      metric_name: metric.name,
      metric_value: Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value),
      metric_id: metric.id,
      metric_rating: getRating(metric.name, metric.value),
    });
  });

  return null;
}
